import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Key, X } from 'lucide-react';

/**
 * ReturningClientBanner — surfaces the forgot-password path for returning clients
 * who can't get back in. Two variants:
 *
 *   variant="login"     → always visible, no dismiss. Sits above the login form
 *                         on PortalLogin.js. Doesn't render its own button — the
 *                         existing "Forgot password?" link in the form is the CTA.
 *   variant="dashboard" → dismissible per browser session via sessionStorage.
 *                         Renders its own "Reset Password" button linking to
 *                         /portal/forgot-password.
 *
 * Both share amber/info styling and a key icon.
 */

const SESSION_KEY = 'returning_client_banner_dismissed';

const ReturningClientBanner = ({ variant = 'login' }) => {
  const [dismissed, setDismissed] = useState(() => {
    if (variant !== 'dashboard') return false;
    try {
      return sessionStorage.getItem(SESSION_KEY) === '1';
    } catch (e) {
      return false;
    }
  });

  if (variant === 'dashboard' && dismissed) return null;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch (e) {
      // sessionStorage may be unavailable (private mode); fall back to in-memory dismiss
    }
    setDismissed(true);
  };

  const copy =
    variant === 'login'
      ? 'Portal security has been upgraded. Returning clients: if you can’t log in, click Forgot Password below to reset access.'
      : 'If you’re locked out or can’t remember your password, use Forgot Password to reset.';

  return (
    <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-4 mb-4 flex items-start gap-3">
      <div className="p-1.5 bg-amber-500/20 rounded-lg flex-shrink-0 mt-0.5">
        <Key className="text-amber-300" size={16} />
      </div>
      <div className="flex-1 text-sm text-amber-200 leading-relaxed">
        {copy}
        {variant === 'dashboard' && (
          <div className="mt-2">
            <Link
              to="/portal/forgot-password"
              className="inline-block px-3 py-1.5 bg-amber-500 text-virgo-navy-900 text-xs font-semibold rounded-lg hover:bg-amber-400 transition-colors"
            >
              Reset Password
            </Link>
          </div>
        )}
      </div>
      {variant === 'dashboard' && (
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="text-amber-400/70 hover:text-amber-200 transition-colors flex-shrink-0"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
};

export default ReturningClientBanner;
